import React from 'react';
import i18next from 'i18next';
import queryString from 'query-string';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';

import { getFormat, twoDigits, makeGraph } from 'lib/commonLib';
import { dailyTopMediaGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('지출금액'),
    data: { bar: ['ADVRTS_AMT'] },
    yAxisData: {
      left: { ADVRTS_AMT: true },
      right: {},
    },
  },
  {
    title: i18next.t('구좌노출수'),
    data: { bar: ['PAR_EPRS_CNT'] },
    yAxisData: {
      left: { PAR_EPRS_CNT: true },
      right: {},
    },
  },
  {
    title: i18next.t('CTR'),
    data: { bar: ['CLICK_RATE'] },
    yAxisData: {
      left: { CLICK_RATE: true },
      right: {},
    },
  },
  {
    title: i18next.t('ECPM'),
    data: { bar: ['ECPM'] },
    yAxisData: {
      left: { ECPM: true },
      right: {},
    },
  },
  {
    title: i18next.t('광고주 수'),
    data: { bar: ['ADVER_CNT'] },
    yAxisData: {
      left: { ADVER_CNT: true },
      right: {},
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
      graphComponent: {},
      selectedBox: {},
      selectedIdx: {},
    };
  }

  getData = () => {
    const { params, locale, t, location } = this.props;
    const { uri } = queryString.parse(location.search);

    const { sDate, eDate } = params;
    const format = getFormat(locale);
    let nData = [];
    this.setState({
      ...this.state,
      loading: true,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    dailyTopMediaGraph({ ...params, uri })
      .then((response) => {
        const { data } = response.data;
        let i = 0;
        for (var key in data) {
          nData[i] = {
            graphData: data[key],
            userid: data[key].info.CORP_NAME,
          };
          i++;
        }
        this.setState({
          ...this.state,
          primitiveData: nData,
          loading: false,
          error: false,
        });

        for (var k in nData) {
          this.handleSetGraphData(graphList[0], k, 0)();
        }
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const { t, theme } = this.props;
    const dateObj = moment(originalDate, 'YYYYMMDD');
    const month = twoDigits(dateObj.month() + 1);
    const date = dateObj.date();
    const day = dateObj.day();
    let color = '';

    if (day === 0) {
      color = theme.palette.common.red;
    } else if (day === 6) {
      color = theme.palette.common.blue;
    }

    return onlyDay ? (
      `${twoDigits(date)}${t('일')}`
    ) : (
      <span style={{ color }}>{`${t(month + '월')} ${twoDigits(date)}${t(
        '일'
      )}`}</span>
    );
  };

  xAxisTick = (tickProps) => {
    const { theme } = this.props;
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const dateObj = moment(value, 'YYYYMMDD');
    const day = dateObj.day();

    const dayStr = this.makeLabel(true)(value);
    let fill = '';
    //Sunday as 0 and Saturday as 6.
    if (day === 0) {
      fill = theme.palette.common.red;
    } else if (day === 6) {
      fill = theme.palette.common.blue;
    }

    return (
      <text x={x} y={y + 10} fill={fill} textAnchor="middle">
        {dayStr}
      </text>
    );
  };

  /**
   * 팝업
   */
  handleCreatePop = (uri) => {
    window.open(uri, 'pop');
    return false;
  };

  /**
   * 그래프를 만든다.
   */
  handleSetGraphData =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      boxIdx,
      index
    ) =>
    (e) => {
      let { primitiveData } = this.state;
      const customize = {};

      if (Array.isArray(primitiveData) === true) {
        primitiveData = primitiveData[boxIdx].graphData.data;
      }

      if (typeof data !== 'undefined' && primitiveData !== 'undefined') {
        const {
          graphData,
          dataKeys,
          customizeData,
          keyLabel,
          stackId,
          referenceData,
        } = makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            width="100%"
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={boxIdx + '-' + index}
            referenceLine={referenceData}
          />
        );

        let { graphComponent, selectedIdx } = this.state;
        graphComponent[boxIdx] = result;
        selectedIdx[boxIdx] = index;

        this.setState({
          ...this.state,
          selectedIdx: selectedIdx,
          graphComponent: graphComponent,
        });
      }
    };

  render() {
    const { handleRefesh, handleSetGraphData } = this;
    const {
      infoMessage,
      graphComponent,
      selectedIdx,
      primitiveData,
      error,
      loading,
    } = this.state;
    return (
      <>
        {loading === true || typeof primitiveData === 'undefined' ? (
          <CurrentStatus type="loading" option="useMinHeight" />
        ) : (
          <>
            {primitiveData.map((data, boxIndex) => (
              <React.Fragment key={boxIndex}>
                <SwitchingArea
                  key={boxIndex}
                  title={
                    <>
                      <InfoTitleArea
                        title={data.userid}
                        tooltipTitle={infoMessage}
                        iconType="fainfoCircle"
                        iconColor="graph"
                      />
                    </>
                  }
                  handleRefesh={handleRefesh}
                >
                  {error ? (
                    <CurrentStatus type="error" />
                  ) : (
                    <>
                      <ChartButtonWrap>
                        {graphList.map((item, index) => {
                          return (
                            <OutlinedButton
                              key={boxIndex + '-' + index}
                              onClick={handleSetGraphData(
                                item,
                                boxIndex,
                                index
                              )}
                              selected={selectedIdx[boxIndex] === index}
                              disabled={!Boolean(primitiveData)}
                            >
                              {item.title}
                            </OutlinedButton>
                          );
                        })}
                      </ChartButtonWrap>
                      {graphComponent[boxIndex]}
                    </>
                  )}
                </SwitchingArea>
              </React.Fragment>
            ))}
          </>
        )}
      </>
    );
  }
}

export default withRouter(
  withTranslation()(
    connect(
      ({ locale }) => ({
        locale: locale.locale,
      }),
      null
    )(withTheme(StatisticsGraph))
  )
);
